import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import HomePageVitrine from "./pages/Home/Home";
import LoginPage from "./pages/LoginPage/LoginPage";
import RegisterPage from "./pages/RegisterPage/RegisterPage";
import TeacherRegisterPage from "./pages/RegisterPage/TeacherRegisterPage";

import ResetPwd from "./pages/ResetPwd/ResetPwd";
import ForgotPasswordPage from "./pages/ResetPwd/ForgotPasswordPage";
import { UserProvider } from "./pages/Home/UserContext";
import PrimaryColorProvider from "./hooks/PrimaryColorProvider.jsx";
import { NotificationToastProvider } from "./components/Notification/NotificationManager.jsx";
import { SidebarProvider } from "./contexts/SidebarContext.js";

import Navbar3 from "./pages/Home/Navbar3/Navbar3";
import Footer from "./pages/Home/Footer";

import { MessageProvider } from "./hooks/MessageContext.js";
import { ContactFormProvider } from "./hooks/ContactFormContext.js";

import ErrorPage from "./pages/ErrorPage/ErrorPage";

// Documents
import ConditionsUtilisationPage from "./pages/rgpd/ConditionsUtilisationPage.jsx";
import PolitiqueConfidentialitePage from "./pages/rgpd/PolitiqueConfidentialitePage.jsx";
import FaqPage from "./pages/rgpd/FaqPage.jsx";
import MentionsLegalesPage from "./pages/rgpd/MentionsLegalesPage.jsx";

// Pages Administrateurs
import AsideMenu from "./components/AsideMenu/AsideMenu"; // Menu latéral
import MainContent from "./components/MainContent/MainContent"; // Contenu principal

// PresencePro
import Profile from "./pages/Profile/Profile";
import VotreClub from "./pages/PresencePro/VotreClub/VotreClub";
import DojoGroupe from "./pages/PresencePro/DojoGroupe/DojoGroupe";
import ManageMembers from "./pages/PresencePro/ManageMembers/ManageMembers.jsx";
import AttendancePage from "./pages/PresencePro/AttendancePage/AttendancePage.jsx";
import AffichagePresence from "./pages/PresencePro/AffichagePresence/AffichagePresence.jsx";
import PricingPage from "./pages/PresencePro/PricingPage/PricingPage.jsx";
import MessagerieJudoka from "./pages/PresencePro/MessagerieJudoka/MessagerieJudoka.jsx";
import StatistiquesAttendances from "./pages/PresencePro/StatistiquesAttendances/StatistiquesAttendances.jsx";
import ManageTeachers from "./pages/PresencePro/ManageTeachers/ManageTeachers.jsx";
import MySubscriptionPage from "./pages/PresencePro/Subscription/MySubscriptionPage.jsx";

// PagesAdministrateurs

// ... importez d'autres pages si nécessaire
import "./App.css";
import "./bulma.min.css";

const stripePromise = loadStripe(
  "pk_test_51POgHVEr14Icaey6JeVMxJf9bAJS9AxDAGUTSDI80b01LVDorrxErNHC1HbTU3WRgJfPD3P40daULyEzeakVDBfk00fHPS7sVp"
);

const App = () => {
  // Gestion des states
  const [userInfo, setUserInfo] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(true);

  useEffect(() => {
    const getLocalStorageItem = (key) => {
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (error) {
        console.error(
          "Erreur lors de la lecture du localStorage pour la clé",
          key,
          ":",
          error
        );
        return null;
      }
    };

    const storedUserInfo = getLocalStorageItem("userInfo");
    if (storedUserInfo) {
      setUserInfo(storedUserInfo);
    } else {
      console.error(
        "Les informations de l'utilisateur stockées sont introuvables ou corrompues."
      );
      // Actions supplémentaires ici, comme une redirection ou une récupération depuis un serveur
    }
  }, []);

  const RequireAuth = ({ children, roles }) => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo")); // Assure-toi que userInfo est stocké dans localStorage
    const location = useLocation();

    if (!userInfo) {
      return <div>Chargement...</div>; // Ou un composant de chargement plus sophistiqué
    }

    if (roles && !roles.includes(userInfo.role_id)) {
      // Rôle non autorisé donc redirection vers une page d'erreur ou d'accueil
      return <Navigate to="/unauthorized" replace />;
    }

    return children;
  };

  // Pour l'exemple, nous allons simuler un utilisateur connecté
  const user = {
    id: 1,
    nom: "Doe",
    prenom: "John",
    email: "JohnDoe@gmail.com",
    photoURL: "path_to_photo.jpg", // Remplace par le chemin réel de la photo
    role_id: 1, // 1 = Administrateur
    userId: 1,
  };

  return (
    <UserProvider>
      <Router>
        <Elements stripe={stripePromise}>
          <MessageProvider>
            <ContactFormProvider>
              <PrimaryColorProvider>
                <NotificationToastProvider>
                  <SidebarProvider>
                    <div className="flex flex-col min-h-screen bg-base-200 dark:bg-gray-600 dark:text-gray-200">
                      <Navbar3
                        user={{
                          nom: userInfo?.nom ?? "Chargement...",
                          prenom: userInfo?.prenom ?? "",
                          role_id: userInfo?.role_id ?? 0,
                          photoURL: userInfo?.photoURL ?? "",
                          userId: userInfo?.id ?? 0,
                        }}
                      />
                      <Routes>
                        {/* Route pour la page d'accueil du site vitrine */}
                        <Route path="/" element={<HomePageVitrine />} />

                        {/* Route pour la page de Pricing */}
                        <Route
                          path="/formules-tarifs"
                          element={<PricingPage />}
                        />

                        {/* Routes administratives protégées */}
                        <Route
                          path="/appli/*"
                          element={
                            <RequireAuth>
                              <div className="flex">
                                <AsideMenu />
                                <MainContent />
                              </div>
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Votre Club */}
                        <Route
                          path="/votre-club"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 7]}>
                              <VotreClub />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Mon Abonnement */}
                        <Route
                          path="/mon-abonnement"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 7]}>
                              <MySubscriptionPage />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Dojo Groupe */}
                        <Route
                          path="/dojo-groupe"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 7]}>
                              <DojoGroupe />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Gestion des Membres */}
                        <Route
                          path="/gestion-membres"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 7]}>
                              <ManageMembers />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Gestion des Présences */}
                        <Route
                          path="/gestion-presences"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7]}>
                              <AttendancePage />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page d'Affichage des Présences */}
                        <Route
                          path="/affichage-presences"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7]}>
                              <AffichagePresence />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Statistiques des Présences */}
                        <Route
                          path="/statistiques-presences"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7]}>
                              <StatistiquesAttendances />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Gestion des Enseignants */}
                        <Route
                          path="/gestion-enseignants"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 7]}>
                              <ManageTeachers />
                            </RequireAuth>
                          }
                        />

                        {/* Route pour la page de Profil Utilisateur */}
                        <Route
                          path="/profile-utilisateur"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7]}>
                              <Profile />
                            </RequireAuth>
                          }
                        />

                        {/* Messagerie Judoka */}
                        <Route
                          path="/messagerie-judoka"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7]}>
                              <MessagerieJudoka />
                            </RequireAuth>
                          }
                        />

                        {/* /////////////////////////////////////////////////////////// */}

                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route
                          path="/register/:token"
                          element={<TeacherRegisterPage />}
                        />
                        <Route
                          path="/reset-password/:token"
                          element={<ResetPwd />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPasswordPage />}
                        />
                        <Route path="/404" element={<ErrorPage />} />
                        <Route path="*" element={<ErrorPage />} />
                        <Route
                          path="/conditions-utilisation"
                          element={<ConditionsUtilisationPage />}
                        />
                        <Route
                          path="/politique-confidentialite"
                          element={<PolitiqueConfidentialitePage />}
                        />
                        <Route path="/faq" element={<FaqPage />} />
                        <Route
                          path="/mentions-legales"
                          element={<MentionsLegalesPage />}
                        />
                      </Routes>
                      <Footer />
                    </div>
                  </SidebarProvider>
                </NotificationToastProvider>
              </PrimaryColorProvider>
            </ContactFormProvider>
          </MessageProvider>
        </Elements>
      </Router>
    </UserProvider>
  );
};

export default App;
